import { UNSEATED_GROUP_ID } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { SeatingTicket } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import { indexToSeatNumber } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';

type TicketUpdate = [string, SeatingTicket];

const isInUnseated = (ticket: SeatingTicket): boolean =>
    ticket.seating_group_id === UNSEATED_GROUP_ID;

const bySeatNumber = (a: SeatingTicket, b: SeatingTicket): number =>
    (a.seat_number || 0) - (b.seat_number || 0);

const bySeatNumberDesc = (a: SeatingTicket, b: SeatingTicket): number =>
    (b.seat_number || 0) - (a.seat_number || 0);

const toTicketWithNewSeat = (
    ticket: SeatingTicket,
    newSeat: number,
    forceUnseated: boolean = false,
): SeatingTicket => ({
    ...ticket,
    seat_number: newSeat,
    ...(forceUnseated && { seating_group_id: UNSEATED_GROUP_ID }),
});

const toSwapUpdates = (
    tickets: Record<string, SeatingTicket>,
    incomingIds: string[],
    targetTicket: SeatingTicket,
    startSeat: number,
): Record<string, SeatingTicket> => {
    const updates: TicketUpdate[] = [
        ...incomingIds.map(
            (id, index): TicketUpdate => [
                id,
                toTicketWithNewSeat(tickets[id], startSeat + indexToSeatNumber(index)),
            ],
        ),
        [targetTicket.id, toTicketWithNewSeat(targetTicket, startSeat)],
    ];

    return Object.fromEntries(updates);
};

const trySwapTransformation = (
    tickets: Record<string, SeatingTicket>,
    incomingTicketIds: string[],
    targetSeatNumber: number,
): Record<string, SeatingTicket> | null => {
    const allInUnseated = incomingTicketIds.every(
        id => tickets[id]?.seating_group_id === UNSEATED_GROUP_ID,
    );
    if (!allInUnseated) {
        return null;
    }

    const firstTicket = tickets[incomingTicketIds[0]];
    if (!firstTicket?.seat_number) {
        return null;
    }

    const seatNumbers = incomingTicketIds
        .map(id => tickets[id]?.seat_number || 0)
        .filter(seat => seat > 0);

    const start = Math.min(...seatNumbers);
    const end = Math.max(...seatNumbers);

    if (targetSeatNumber !== end + 1) {
        return null;
    }

    const targetTicket = Object.values(tickets).find(
        t => isInUnseated(t) && t.seat_number === targetSeatNumber,
    );

    if (!targetTicket || incomingTicketIds.includes(targetTicket.id)) {
        return null;
    }

    return toSwapUpdates(tickets, incomingTicketIds, targetTicket, start);
};

const toUnseatedPlacements = (
    tickets: Record<string, SeatingTicket>,
    incomingTicketIds: string[],
    targetPosition: number,
): Record<string, SeatingTicket> => {
    const allUnseatedTickets = Object.values(tickets).filter(isInUnseated).sort(bySeatNumber);

    const remainingTickets = allUnseatedTickets.filter(t => !incomingTicketIds.includes(t.id));

    const incomingTickets = incomingTicketIds.map(id => tickets[id]).filter(Boolean);

    const beforeTarget = remainingTickets.filter(t => (t.seat_number || 0) < targetPosition);
    const afterTarget = remainingTickets.filter(t => (t.seat_number || 0) >= targetPosition);

    const reorderedTickets = [...beforeTarget, ...incomingTickets, ...afterTarget];

    const updates: TicketUpdate[] = reorderedTickets
        .map(
            (ticket, index): TicketUpdate => [
                ticket.id,
                toTicketWithNewSeat(ticket, indexToSeatNumber(index), !isInUnseated(ticket)),
            ],
        )
        .filter(([id, updated]) => {
            const original = tickets[id];
            return (
                original.seat_number !== updated.seat_number ||
                original.seating_group_id !== updated.seating_group_id
            );
        });

    return Object.fromEntries(updates);
};

const reshuffleUnseated = (
    tickets: Record<string, SeatingTicket>,
    incomingTicketIds: string[],
    targetSeatNumber: number,
): Record<string, SeatingTicket> => {
    if (incomingTicketIds.length === 0) return {};

    const swapUpdates = trySwapTransformation(tickets, incomingTicketIds, targetSeatNumber);
    if (swapUpdates) return swapUpdates;

    const firstTicket = tickets[incomingTicketIds[0]];
    const sharedGroupId = firstTicket?.ticket_group_id;

    const allFromSameGroup =
        sharedGroupId &&
        incomingTicketIds.every(id => {
            const ticket = tickets[id];
            return (
                ticket &&
                ticket.ticket_group_id === sharedGroupId &&
                ticket.seating_group_id !== UNSEATED_GROUP_ID
            );
        });

    const groupMembers = allFromSameGroup
        ? Object.values(tickets)
              .filter(t => t.ticket_group_id === sharedGroupId && isInUnseated(t))
              .sort(bySeatNumberDesc)
        : [];

    const lastGroupMember = groupMembers[0];
    const groupConsolidationPosition = lastGroupMember
        ? indexToSeatNumber(lastGroupMember.seat_number || 0)
        : targetSeatNumber;

    const targetPosition = allFromSameGroup ? groupConsolidationPosition : targetSeatNumber;

    return toUnseatedPlacements(tickets, incomingTicketIds, targetPosition);
};

export { reshuffleUnseated };
